import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { reportingLeLensCataloguePath } from '~/consts/reportingLeLensApi';

import { useDryRunReportingLeLensDataset } from '~/hooks/apis/reporting/useLeLensDatasetChecks';
import {
  lensCatalogueQueryKeys,
  useGetLensCatalogue,
  useGetLensCatalogueDataset,
} from '~/hooks/apis/reporting/useLensCatalogue';

import { request } from '~/services/common';

import type { LensCatalogueDataset, LensCatalogueDatasetSummary, LensDataset, LensDatasetColumn } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { bestDatasetSql, describeRejection } from '../../buildPlan';
import DatasetFieldGroups from '../../catalogue/DatasetFieldGroups';
import { MEASURE_GROUP_LABEL, groupMeasuresByKind } from '../../catalogue/catalogueFormat';

import { extractCannotLine, extractGrainLine } from './extendPrompt';
import { newDatasetPrompt } from './newDatasetPrompt';
import { streamDatasetSqlAnswer } from './streamAiSql';

/**
 * F6, "+ New". A person who has never seen SQL should be able to pick a governed dataset and
 * never see a query, a grain, a manifest or a dry run (00_PLAN.md's user-friendliness test): the
 * picker shows every governed dataset from the catalogue first, one card each, and saves the
 * server's own ready-to-save draft directly on "Use {name}", no dry run, since the server already
 * declared its columns. "Write a custom query instead" reveals the pre-existing free-SQL path
 * (paste, or ask askRichard), which still ends in a dry run and a confirmed grain before it can
 * be named or saved: that guard exists because nobody, human or model, vouches for a hand-written
 * query's columns the way the catalogue vouches for its own. The pin entry (`useCreatePinDataset`,
 * below) is a hook the chat side calls directly, no UI of its own, and is unaffected by any of
 * this: a one-card pin is never a governed pick.
 */

export type NewDatasetDraft = Omit<LensDataset, 'id'>;

type Entry = 'choose' | 'sql';

type Declared = {
  columns: Array<LensDatasetColumn>;
  dryRun: NonNullable<NewDatasetDraft['dryRun']>;
  sourceTable?: string;
};

export type NewDatasetProps = {
  /** Persists the draft as a new dataset on the board and returns its id. */
  onSave: (draft: NewDatasetDraft) => Promise<string>;
  /** Lets the caller immediately select the saved dataset for the card that
   * opened this composer. Keeping this as an explicit hand-off avoids a new
   * dataset being saved successfully while the open card draft still points
   * at no data. */
  onSaved?: (datasetId: string) => void;
  onClose: () => void;
  /** Prefilled from F4/F8's "Create a new dataset for this instead". */
  initialSql?: string;
  /** "Browse the full catalogue": leaves the picker for the Data catalogue's own list screen.
   *  Hidden when not wired, the same way every other optional action in this pane is. */
  onBrowseCatalogue?: () => void;
};

/** The first two governed datasets show in full (name, purpose, table, what you can measure,
 *  refresh and field count, "See full details"); the rest show as compact rows. Two is enough
 *  for today's catalogue to fit without scrolling and still give the two most-reached-for
 *  datasets their measure lists (DESIGN_D.md section 2). */
const FULL_ENTRY_COUNT = 2;

export default function NewDataset({ initialSql, onBrowseCatalogue, onClose, onSave, onSaved }: NewDatasetProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [entry, setEntry] = useState<Entry>('choose');
  const [sql, setSql] = useState(initialSql ?? '');
  const [request_, setRequest] = useState('');
  const [asking, setAsking] = useState(false);
  const [askFailure, setAskFailure] = useState<string | undefined>(undefined);
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunFailure, setDryRunFailure] = useState<string | undefined>(undefined);
  const [declared, setDeclared] = useState<Declared | undefined>(undefined);
  const [name, setName] = useState('');
  const [grainKeys, setGrainKeys] = useState<Array<string>>([]);
  const [saving, setSaving] = useState(false);
  const [pickingGovernedId, setPickingGovernedId] = useState<string | undefined>(undefined);
  const [governedFailure, setGovernedFailure] = useState<string | undefined>(undefined);
  // "See full details" on an entry expands it inline, in place of the list, the same surface
  // Picker_Detail.png draws: never a second dialog stacked over the first.
  const [expandedId, setExpandedId] = useState<string | undefined>(undefined);
  const dryRun = useDryRunReportingLeLensDataset();
  const queryClient = useQueryClient();
  const catalogue = useGetLensCatalogue(entry === 'choose');
  const datasets = catalogue.data?.datasets ?? [];
  const fullEntryIds = datasets.slice(0, FULL_ENTRY_COUNT).map((dataset) => dataset.id);
  // Fetched eagerly (not only on "See full details") so the two full entries can show their
  // measures grouped Money/Counts/Rates from the first paint, falling back to the summary's
  // flat measureLabels only for the instant before this resolves.
  const fullEntryDetailA = useGetLensCatalogueDataset(entry === 'choose' ? fullEntryIds[0] : undefined);
  const fullEntryDetailB = useGetLensCatalogueDataset(entry === 'choose' ? fullEntryIds[1] : undefined);
  const expandedDetail = useGetLensCatalogueDataset(entry === 'choose' ? expandedId : undefined);
  const detailFor = (id: string): LensCatalogueDataset | undefined => {
    if (fullEntryDetailA.data?.id === id) {
      return fullEntryDetailA.data;
    }
    if (fullEntryDetailB.data?.id === id) {
      return fullEntryDetailB.data;
    }
    if (expandedDetail.data?.id === id) {
      return expandedDetail.data;
    }
    return undefined;
  };

  const reset = () => {
    setEntry('choose');
    setSql(initialSql ?? '');
    setRequest('');
    setAskFailure(undefined);
    setDryRunFailure(undefined);
    setDeclared(undefined);
    setName('');
    setGrainKeys([]);
    setGovernedFailure(undefined);
    setExpandedId(undefined);
  };

  const pickGoverned = async (summary: LensCatalogueDatasetSummary) => {
    setGovernedFailure(undefined);
    setPickingGovernedId(summary.id);
    try {
      const detail = await queryClient.fetchQuery({
        queryFn: async () =>
          (await request(resolveReportingLeLensUrl(reportingLeLensCataloguePath(summary.id)), {
            method: 'GET',
          })) as LensCatalogueDataset,
        queryKey: lensCatalogueQueryKeys.detail(summary.id),
      });
      const datasetId = await onSave(detail.draft);
      onSaved?.(datasetId);
      reset();
      onClose();
    } catch (error) {
      setGovernedFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setPickingGovernedId(undefined);
    }
  };

  const runDryRun = async (candidateSql: string, suggestedGrain: Array<string>, suggestedName: string) => {
    setDryRunFailure(undefined);
    setDryRunning(true);
    try {
      const result = await dryRun.mutateAsync(candidateSql);
      setSql(candidateSql);
      setDeclared({
        columns: result.columns,
        dryRun: {
          at: new Date().toISOString(),
          bytesProcessed: result.bytesProcessed,
          referencedTables: result.referencedTables,
        },
        sourceTable: result.referencedTables[0],
      });
      // A suggested grain may name a column the dry run did not in fact return (the model or the
      // catalogue template drifted from the live schema); keep only what is really there.
      const declaredNames = new Set(result.columns.map((column) => column.name));
      setGrainKeys(suggestedGrain.filter((key) => declaredNames.has(key)));
      setName((current) => current || suggestedName);
    } catch (error) {
      setDryRunFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setDryRunning(false);
    }
  };

  const askModel = async () => {
    if (!request_.trim()) {
      return;
    }
    setAsking(true);
    setAskFailure(undefined);
    const { answer, sqlBlocks, streamError } = await streamDatasetSqlAnswer(newDatasetPrompt(request_.trim()));
    setAsking(false);
    if (streamError) {
      setAskFailure(streamError);
      return;
    }
    const cannot = extractCannotLine(answer);
    const candidate = bestDatasetSql(sqlBlocks, []);
    if (!candidate) {
      setAskFailure(cannot ?? describeRejection(sqlBlocks, []));
      return;
    }
    await runDryRun(candidate, extractGrainLine(answer) ?? [], '');
  };

  const toggleGrainKey = (column: string) => {
    setGrainKeys((current) =>
      current.includes(column) ? current.filter((key) => key !== column) : [...current, column],
    );
  };

  const canSave = Boolean(declared) && Boolean(name.trim()) && grainKeys.length > 0;

  const save = async () => {
    if (!declared) {
      return;
    }
    setSaving(true);
    try {
      const datasetId = await onSave({
        columns: declared.columns,
        dryRun: declared.dryRun,
        grain: { keys: grainKeys },
        name: name.trim(),
        source: { kind: 'sql', ...(declared.sourceTable ? { table: declared.sourceTable } : {}) },
        sql,
      });
      onSaved?.(datasetId);
      reset();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (entry === 'choose' && expandedId) {
    const summary = datasets.find((dataset) => dataset.id === expandedId);
    if (summary) {
      const detail = detailFor(expandedId);
      return (
        <Stack spacing={2}>
          <Button
            onClick={() => setExpandedId(undefined)}
            size="small"
            startIcon={<ChevronLeftIcon fontSize="small" />}
            sx={{ alignSelf: 'flex-start' }}
          >
            All governed datasets
          </Button>
          <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }} variant="h6">
                {summary.name}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                {summary.purpose}
              </Typography>
              <Typography color="text.secondary" sx={{ display: 'block', mt: 0.5 }} variant="caption">
                Governed dataset · refreshed {summary.refresh} · {summary.fieldCount} fields · {summary.table}
              </Typography>
            </Box>
            <Button
              disabled={Boolean(pickingGovernedId)}
              onClick={() => pickGoverned(summary)}
              sx={{ flexShrink: 0 }}
              variant="contained"
            >
              {pickingGovernedId === summary.id ? 'Using…' : 'Use in this Lens'}
            </Button>
          </Stack>

          {governedFailure && <Alert severity="warning">{governedFailure}</Alert>}

          {detail ? (
            <DatasetFieldGroups fields={detail.fields} />
          ) : (
            <Stack spacing={1}>
              <Skeleton height={80} variant="rounded" />
              <Skeleton height={80} variant="rounded" />
            </Stack>
          )}

          <Alert severity="info" variant="outlined">
            Every measure carries its own rule. Combine follows the rule, so a count of orders is never summed and a
            rate is never averaged.
          </Alert>
        </Stack>
      );
    }
  }

  if (entry === 'choose') {
    const fullEntries = datasets.slice(0, FULL_ENTRY_COUNT);
    const compactEntries = datasets.slice(FULL_ENTRY_COUNT);

    return (
      <Stack spacing={2}>
        <Box>
          <Typography sx={{ fontWeight: 700 }} variant="h6">
            Choose the data for this Lens
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
            A Lens reads one governed dataset, and every card on it is a view of that data.
          </Typography>
        </Box>

        {catalogue.isLoading && (
          <Stack spacing={1}>
            <Skeleton height={140} variant="rounded" />
            <Skeleton height={140} variant="rounded" />
          </Stack>
        )}

        {catalogue.isError && (
          <Alert severity="warning">
            The catalogue is not available right now. You can still write a custom query.
          </Alert>
        )}

        {fullEntries.length > 0 && (
          <Box>
            <Typography
              color="text.secondary"
              sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.06em', mb: 1, textTransform: 'uppercase' }}
              variant="caption"
            >
              Governed datasets
            </Typography>
            <Stack spacing={1.5}>
              {fullEntries.map((dataset) => {
                const detail = detailFor(dataset.id);
                const groups = detail ? groupMeasuresByKind(detail.fields) : undefined;
                return (
                  <Box key={dataset.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
                          {dataset.name}
                        </Typography>
                        <Typography sx={{ display: 'block', mt: 0.25 }} variant="body2">
                          {dataset.purpose}
                        </Typography>
                        <Typography
                          sx={{ color: tokens.accent, display: 'block', fontSize: '11.5px', mt: 0.5, opacity: 0.68 }}
                        >
                          {dataset.table}
                        </Typography>
                      </Box>
                      <Button
                        disabled={Boolean(pickingGovernedId)}
                        onClick={() => pickGoverned(dataset)}
                        sx={{ flexShrink: 0 }}
                        variant="contained"
                      >
                        {pickingGovernedId === dataset.id ? 'Using…' : `Use ${dataset.name}`}
                      </Button>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography
                      color="text.secondary"
                      sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                      variant="caption"
                    >
                      What you can measure
                    </Typography>
                    {groups ? (
                      <Grid container spacing={0.75} sx={{ mt: 0.25 }}>
                        {groups.map(([group, labels]) => (
                          <React.Fragment key={group}>
                            <Grid size={{ xs: 2.5 }}>
                              <Typography color="text.secondary" sx={{ fontWeight: 700 }} variant="caption">
                                {MEASURE_GROUP_LABEL[group].toUpperCase()}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 9.5 }}>
                              <Typography variant="caption">{labels.join(', ')}</Typography>
                            </Grid>
                          </React.Fragment>
                        ))}
                      </Grid>
                    ) : (
                      <Typography color="text.secondary" sx={{ display: 'block', mt: 0.25 }} variant="caption">
                        {dataset.measureLabels.join(', ')}
                      </Typography>
                    )}

                    <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ mt: 1.25 }}>
                      <Typography color="text.secondary" variant="caption">
                        Refreshed {dataset.refresh} · {dataset.fieldCount} fields
                      </Typography>
                      <Typography
                        component="button"
                        onClick={() => setExpandedId(dataset.id)}
                        sx={{
                          background: 'none',
                          border: 0,
                          color: 'primary.main',
                          cursor: 'pointer',
                          font: 'inherit',
                          fontWeight: 600,
                          p: 0,
                        }}
                        type="button"
                        variant="caption"
                      >
                        See full details
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {compactEntries.length > 0 && (
          <Stack divider={<Divider />} spacing={0}>
            {compactEntries.map((dataset) => (
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                key={dataset.id}
                spacing={2}
                sx={{ py: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }} variant="body2">
                    {dataset.name}
                  </Typography>
                  <Typography color="text.secondary" noWrap variant="caption">
                    {dataset.purpose}
                  </Typography>
                </Box>
                <Button
                  disabled={Boolean(pickingGovernedId)}
                  onClick={() => pickGoverned(dataset)}
                  size="small"
                  sx={{ flexShrink: 0 }}
                  variant="outlined"
                >
                  {pickingGovernedId === dataset.id ? 'Using…' : 'Use'}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}

        {onBrowseCatalogue && (
          <Button
            onClick={onBrowseCatalogue}
            size="small"
            startIcon={<ExploreOutlinedIcon fontSize="small" />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Browse the full catalogue
          </Button>
        )}

        {governedFailure && <Alert severity="warning">{governedFailure}</Alert>}

        <Divider />

        <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography
              component="button"
              onClick={() => setEntry('sql')}
              sx={{ background: 'none', border: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, p: 0 }}
              type="button"
              variant="body2"
            >
              Write a custom query instead
            </Typography>
            <Typography color="text.secondary" sx={{ display: 'block', mt: 0.25 }} variant="caption">
              You write and maintain the SQL. Nobody vouches for what its columns mean, and askRichard cannot build
              cards from it.
            </Typography>
          </Box>
          <Button onClick={onClose} size="small">
            Cancel
          </Button>
        </Stack>
      </Stack>
    );
  }

  if (entry === 'sql' && !declared) {
    return (
      <Stack spacing={1}>
        <Typography color="text.secondary" variant="caption">
          Custom query
        </Typography>
        <TextField
          disabled={asking || dryRunning}
          fullWidth
          multiline
          onChange={(event) => setSql(event.target.value)}
          placeholder="Paste SQL, or describe what you need below and ask the model"
          rows={6}
          slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: 12.5 } } }}
          value={sql}
        />
        <Stack direction="row" spacing={1}>
          <TextField
            disabled={asking}
            fullWidth
            onChange={(event) => setRequest(event.target.value)}
            placeholder="e.g. daily bookings and margin by market"
            size="small"
            value={request_}
          />
          <Button disabled={asking || !request_.trim()} onClick={askModel} variant="outlined">
            {asking ? 'Asking…' : 'Ask askRichard'}
          </Button>
        </Stack>
        {askFailure && <Alert severity="warning">{askFailure}</Alert>}
        <Stack direction="row" spacing={1}>
          <Button onClick={() => setEntry('choose')} size="small">
            Back
          </Button>
          <Button disabled={dryRunning || !sql.trim()} onClick={() => runDryRun(sql, [], '')} variant="contained">
            Dry run
          </Button>
        </Stack>
        {dryRunning && <LinearProgress />}
        {dryRunFailure && <Alert severity="warning">{dryRunFailure}</Alert>}
      </Stack>
    );
  }

  if (!declared) {
    return null;
  }

  return (
    <Stack spacing={1.25}>
      <Alert severity="success">
        Dry run: {declared.columns.length} columns, {(declared.dryRun.bytesProcessed / (1024 * 1024)).toFixed(1)} MB
        scanned.
      </Alert>
      <TextField label="Name" onChange={(event) => setName(event.target.value)} size="small" value={name} />
      <Box>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
          Confirm the grain: which columns make one row unique
        </Typography>
        <Stack>
          {declared.columns.map((column) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={grainKeys.includes(column.name)}
                  onChange={() => toggleGrainKey(column.name)}
                  size="small"
                />
              }
              key={column.name}
              label={
                <Typography sx={{ fontFamily: 'monospace' }} variant="body2">
                  {column.name}{' '}
                  <Typography color="text.secondary" component="span" variant="caption">
                    {column.type}
                  </Typography>
                </Typography>
              }
            />
          ))}
        </Stack>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button onClick={reset} size="small">
          Back
        </Button>
        <Button disabled={!canSave || saving} onClick={save} variant="contained">
          Save
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * The pin entry: no UI of its own, so the chat side (B3) can create a
 * one-card dataset directly from `{ sql, title }` and get its id back. The
 * dry run is the only network call; naming follows F6's "Card query:
 * {title}" convention for a one-card dataset, and `onSave` (the mount
 * site's own persistence, the same shape `NewDataset`'s Save uses) is what
 * actually generates and returns the id.
 */
export function useCreatePinDataset(onSave: (draft: NewDatasetDraft) => Promise<string>) {
  const dryRun = useDryRunReportingLeLensDataset();
  return async ({ sql, title }: { sql: string; title: string }): Promise<string> => {
    const result = await dryRun.mutateAsync(sql);
    return onSave({
      columns: result.columns,
      dryRun: {
        at: new Date().toISOString(),
        bytesProcessed: result.bytesProcessed,
        referencedTables: result.referencedTables,
      },
      name: `Card query: ${title}`,
      source: { kind: 'sql', ...(result.referencedTables[0] ? { table: result.referencedTables[0] } : {}) },
      sql,
    });
  };
}
